import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Collapse,
} from "@mui/material";
import { SendOutlined } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import {
  useAddReplyToNoteMutation,
  useUpdateReplyInNoteMutation,
} from "store/services/noteApi";
import { NoteReplyItem } from "./NoteReplyItem";
import { toast } from "react-toastify";
import { useAuth } from "hooks";

const INITIAL_STATE = { noteReplyText: "" };

export const NoteReplies = ({
  noteId,
  replies = [],
  openReplyBoxById,
  isActive,
  showReplies = true,
}) => {
  const { control, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: INITIAL_STATE,
  });
  const { loggedInUser } = useAuth();

  const [editingReplyId, setEditingReplyId] = useState(null);
  const [updatedText, setUpdatedText] = useState("");
  
  const noteReplyText = watch("noteReplyText");
  
  const [addReply, { isLoading: addingNoteReply }] =
  useAddReplyToNoteMutation();
  const [updateReply, { isLoading: updatingReply }] =
  useUpdateReplyInNoteMutation();
  
  if (isActive === false) return null;
  
  const handleAddReply = async (data) => {
    const payload = {
      replyText: data.noteReplyText,
      noteId,
    };

    try {
      await addReply(payload).unwrap();
      reset(INITIAL_STATE);
    } catch (error) {
      console.error("Error adding reply:", error);
    }
  };

  const handleUpdateReply = async (replyId) => {
    if (!updatedText.trim()) return;

    const payload = {
      replyText: updatedText,
      replyId,
      noteId,
    };

    try {
      await updateReply(payload).unwrap();

      setEditingReplyId(null);
      setUpdatedText(null);
      reset(INITIAL_STATE);

      toast.success("Reply updated");
    } catch (error) {
      toast.error(
        error.message ?? error?.data?.message ?? "Something went wrong"
      );
      console.error("Error updating reply:", error);
    }
  };

  return (
    <Box >
      <Collapse in={openReplyBoxById === noteId || showReplies}>
        <Box>
          {replies.map((reply) => (
            <Box
              key={reply._id}
              className="reply-item"
              sx={{
                marginTop: "-30px",
              }}
            >
              {editingReplyId === reply._id ? (
                <Box display="flex" alignItems="center">
                  <TextField
                    value={updatedText}
                    onChange={(e) => setUpdatedText(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Edit your reply..."
                  />
                  <IconButton
                    color="primary"
                    onClick={() => handleUpdateReply(reply._id)}
                    disabled={updatingReply || !updatedText}
                  >
                    {updatingReply ? (
                      <CircularProgress size="1rem" color="inherit" />
                    ) : (
                      <SendOutlined />
                    )}
                  </IconButton>
                </Box>
              ) : (
                <Box display="flex" alignItems="center">
                  <NoteReplyItem reply={reply} />
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setEditingReplyId(reply._id);
                      setUpdatedText(reply.replyText);
                    }}
                    disabled={
                      (reply?.user?._id !== loggedInUser?._id)
                    }
                  >
                    {/* <EditOutlined /> */}
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {<Box className="reply-input ml-[80px] mt-1"
        >
          <Controller
            control={control}
            name="noteReplyText"
            render={({ field }) => (
              <TextField
                {...field}
                rows={2}
                onChange={(e) => setValue("noteReplyText", e.target.value)}
                multiline
                fullWidth
                placeholder="Write a reply..."
              />
            )}
          />
          <Box className="flex justify-end">
            <IconButton
              disabled={!noteReplyText}
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleAddReply)}
            >
              {!addingNoteReply ? (
                <SendOutlined />
              ) : (
                <CircularProgress size="1rem" color="inherit" />
              )}
            </IconButton>
          </Box>
        </Box>}
      </Collapse>
    </Box>
  );
};